/**
 * Register Quill custom blots for NewsletterEditor at app startup.
 * Must run before any Quill instance is created (e.g. before lazy-loaded NewsletterEditor).
 *
 * Two pill button blots (try both if one fails):
 * - pillButton: structured data {text, url, variant} - renders via emailPillButtonHtml
 * - emailPillButton: raw HTML string - preserves exact table output
 * - pill-buttons-row: full Lead template CTA row (table + three pills) — keeps buttons horizontal in the editor
 */
import { Quill } from 'react-quill';
import { emailPillButtonHtml } from './emailPillButtonHtml';

const BlockEmbedBase = Quill.import('blots/block/embed');

// Method 1: Structured data blot (like EnhancedButtonBlot) - stores {text, url, variant}
class PillButtonBlot extends BlockEmbedBase {
    static blotName = 'pillButton';
    static tagName = 'div';
    static className = 'ql-pill-button';
    static create(value) {
        const node = super.create();
        node.setAttribute('contenteditable', 'false');
        const data = typeof value === 'object' && value ? value : { text: 'Click', url: '#', variant: 'primary' };
        const { text, url, variant } = data;
        node.innerHTML = emailPillButtonHtml(text || 'Click', url || '#', variant || 'primary');
        return node;
    }
    static value(node) {
        const a = node.querySelector('a');
        if (!a) return {};
        let variant = 'primary';
        const bg = a.style?.backgroundColor || '';
        if (/8[Bb]6[Ff]2[Ee]|139,\s*111,\s*46/i.test(bg)) variant = 'secondary';
        return { text: a.textContent, url: a.href, variant };
    }
}

// Method 2: Raw HTML blot - stores full table HTML string
class EmailPillButtonBlot extends BlockEmbedBase {
    static blotName = 'emailPillButton';
    static tagName = 'div';
    static className = 'ql-email-pill-button';
    static create(html) {
        const node = super.create();
        node.setAttribute('contenteditable', 'false');
        if (typeof html === 'string') node.innerHTML = html;
        return node;
    }
    static value(node) { return node.innerHTML; }
}

class ProductImagesRowBlot extends BlockEmbedBase {
    static blotName = 'product-images-row';
    static tagName = 'div';
    static className = 'ql-product-images-row';
    static create(html) {
        const node = super.create();
        node.setAttribute('contenteditable', 'false');
        node.classList.add('product-images-horizontal');
        if (typeof html === 'string') node.innerHTML = html;
        return node;
    }
    static value(node) { return node.innerHTML; }
}

/** Full `<table class="pill-buttons-row">` row — prevents Quill from splitting nested pill tables vertically */
class PillButtonsRowBlot extends BlockEmbedBase {
    static blotName = 'pill-buttons-row';
    static tagName = 'div';
    static className = 'ql-pill-buttons-row';
    static create(html) {
        const node = super.create();
        node.setAttribute('contenteditable', 'false');
        node.classList.add('pill-buttons-row-wrap');
        if (typeof html === 'string') node.innerHTML = html;
        return node;
    }
    static value(node) { return node.innerHTML; }
}

Quill.register(PillButtonBlot);
Quill.register(EmailPillButtonBlot);
Quill.register(ProductImagesRowBlot);
Quill.register(PillButtonsRowBlot);
